import type { ResultOf } from "gql.tada";
import { graphql } from "@/data-access/graphql";

import {
  FeedArticleProjectionFragment,
  PageInfoFragment,
} from "@/data-access/shared/fragments/feed-article-projection-fragments";

/**
 * Query to get the total number of articles without sidebar filters
 */
export const GetAggregateOfAllArticles = graphql(`
  query getAggregateOfAllArticles($filters: FeedArticleProjectionFilter) {
    feedArticleProjections(filters: $filters) {
      totalCount
    }
  }
`);

export const DeleteFeedArticle = graphql(`
  mutation deleteFeedArticle($input: DeleteFeedArticleInput!) {
    deleteFeedArticle(input: $input) {
      ... on SuccessResponse {
        __typename
        message
      }
      ... on OperationInfo {
        __typename
        messages {
          kind
          message
          field
          code
        }
      }
    }
  }
`);

/**
 * Mutation for updating/editing feed articles
 * Allows overriding article properties like headline, summary, author, publisher, etc.
 * Returns the updated articles in the response
 */
export const UpdateArticle = graphql(
  `
    mutation updateArticle(
      $overrides: OverrideFeedArticleInputType!
      $feedId: ID!
      $feedArticleIds: [ID!]
      $excludedIds: [ID!]
      $projectionFilters: FeedArticleProjectionFilter
    ) {
      feedArticleOverride(
        input: {
          feedId: $feedId
          overrides: $overrides
          feedArticleIds: $feedArticleIds
          excludedIds: $excludedIds
          projectionFilters: $projectionFilters
        }
      ) {
        ... on SuccessResponse {
          __typename
          message
        }
        ... on FeedArticleProjectionListResponse {
          __typename
          feedArticleProjection {
            ...FeedArticleProjectionFragment
          }
        }
        ... on OperationInfo {
          __typename
          messages {
            kind
            message
            field
            code
          }
        }
      }
    }
  `,
  [FeedArticleProjectionFragment],
);

/**
 * Mutation for managing story articles
 * Returns feed articles in the response when successful
 */
export const MoveOrRemoveArticlesFromNarrativeCluster = graphql(
  `
    mutation moveOrRemoveArticlesFromNarrativeCluster(
      $input: ManageStoryArticlesInput!
    ) {
      manageStoryArticlesV2(input: $input) {
        ... on FeedArticleProjectionListResponse {
          __typename
          feedArticleProjection {
            ...FeedArticleProjectionFragment
          }
        }
        ... on OperationInfo {
          __typename
          messages {
            message
          }
        }
        ... on SuccessResponse {
          __typename
          message
        }
      }
    }
  `,
  [FeedArticleProjectionFragment],
);

/**
 * Query for fetching articles from FeedArticleProjection using cursor-based pagination
 * This query uses the projection endpoint which is optimized for read operations
 * and provides effective values (overrides/translations already applied)
 * Uses cursor-based pagination (first/after) for infinite scroll support
 */
export const GetFeedArticlesPagedFromProjection = graphql(
  `
    query getFeedArticlesPagedFromProjection(
      $ordering: [FeedArticleProjectionOrder!]!
      $filters: FeedArticleProjectionFilter
      $first: Int
      $after: String
      $last: Int
      $before: String
    ) {
      feedArticleProjections(
        ordering: $ordering
        filters: $filters
        first: $first
        after: $after
        last: $last
        before: $before
      ) {
        edges {
          cursor
          node {
            ...FeedArticleProjectionFragment
          }
        }
        pageInfo {
          ...PageInfoFragment
        }
        totalCount
      }
    }
  `,
  [FeedArticleProjectionFragment, PageInfoFragment],
);

/**
 * Result type for GetFeedArticlesPagedFromProjection query
 */
export type GetFeedArticlesPagedFromProjectionResult = ResultOf<
  typeof GetFeedArticlesPagedFromProjection
>;

/**
 * Article type from GetFeedArticlesPagedFromProjection query results
 */
export type GetFeedArticlesPagedFromProjectionArticle =
  GetFeedArticlesPagedFromProjectionResult["feedArticleProjections"]["edges"][number]["node"];

export type ArticleItem = GetFeedArticlesPagedFromProjectionArticle;

/** Article prominence derived from the projection query (not from filter/enum types) */
export type ArticleProminenceType = NonNullable<ArticleItem["prominence"]>;

/** Article sentiment polarity derived from the projection query (not from filter/enum types) */
export type ArticleSentimentPolarityType = NonNullable<
  ArticleItem["sentimentPolarity"]
>;

/** Article category derived from the projection query (not from filter/enum types) */
export type ArticleCategoryType = NonNullable<ArticleItem["contentCategory"]>;
